import { useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { PremiumGate } from "@/components/PremiumGate";
import axios from "axios";
import * as XLSX from "xlsx";
import { Upload, FileSpreadsheet, CheckCircle2, AlertCircle, X, Download } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

// Mappatura nomi giorni → indice (0=Lun … 6=Dom)
const DAY_MAP = {
  lun: 0, lunedì: 0, lunedi: 0, monday: 0, mon: 0,
  mar: 1, martedì: 1, martedi: 1, tuesday: 1, tue: 1,
  mer: 2, mercoledì: 2, mercoledi: 2, wednesday: 2, wed: 2,
  gio: 3, giovedì: 3, giovedi: 3, thursday: 3, thu: 3,
  ven: 4, venerdì: 4, venerdi: 4, friday: 4, fri: 4,
  sab: 5, sabato: 5, saturday: 5, sat: 5,
  dom: 6, domenica: 6, sunday: 6, sun: 6,
};

const DAY_LABELS = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];

function parseDay(val) {
  if (val === null || val === undefined || val === "") return null;
  const s = String(val).toLowerCase().trim();
  if (DAY_MAP[s] !== undefined) return DAY_MAP[s];
  // Prova come numero: 1-7 → 0-6, oppure 0-6 diretto
  const n = parseInt(s);
  if (!isNaN(n)) {
    if (n >= 1 && n <= 7) return n - 1;
    if (n >= 0 && n <= 6) return n;
  }
  return null;
}

function downloadTemplate() {
  const headers = ["Nome Cliente", "Indirizzo", "Città", "Giorno Preferito", "Frequenza (settimane)"];
  const examples = [
    ["Mario Rossi", "Via Roma 10", "Milano", "Lunedì", 2],
    ["Azienda SRL", "Corso Italia 5", "Roma", "Mercoledì", 1],
    ["Cliente Esempio", "Via Garibaldi 3", "Napoli", "Venerdì", 3],
  ];
  const ws = XLSX.utils.aoa_to_sheet([headers, ...examples]);
  // Larghezze colonne
  ws["!cols"] = [{ wch: 22 }, { wch: 22 }, { wch: 16 }, { wch: 18 }, { wch: 22 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Clienti");
  XLSX.writeFile(wb, "template_clienti_venditoreElite.xlsx");
}

function UploadContent() {
  const { token } = useAuth();
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [errors, setErrors] = useState([]);
  const [importing, setImporting] = useState(false);
  const [done, setDone] = useState(null);
  const [dragging, setDragging] = useState(false);

  const parseFile = (file) => {
    setFileName(file.name);
    setRows([]); setErrors([]); setDone(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });

        const parsed = [];
        const errs = [];
        // Salta intestazione se presente
        const startRow = data[0] && String(data[0][0]).toLowerCase().includes("nom") ? 1 : 0;

        for (let i = startRow; i < data.length; i++) {
          const row = data[i];
          const name = String(row[0] || "").trim();
          const address = String(row[1] || "").trim();
          const city = String(row[2] || "").trim();
          const dayRaw = row[3];
          const freqRaw = row[4];

          if (!name) continue;
          if (name.length < 2) { errs.push(`Riga ${i + 1}: nome troppo corto ("${name}")`); continue; }

          // Giorno preferito
          const preferred_day = parseDay(dayRaw);
          if (dayRaw !== "" && dayRaw !== undefined && preferred_day === null) {
            errs.push(`Riga ${i + 1}: giorno non riconosciuto ("${dayRaw}") — sarà ignorato`);
          }

          // Frequenza
          let frequency_weeks = 2;
          if (freqRaw !== "" && freqRaw !== undefined) {
            const n = parseInt(String(freqRaw));
            if (!isNaN(n) && n >= 1 && n <= 52) {
              frequency_weeks = n;
            } else {
              errs.push(`Riga ${i + 1}: frequenza non valida ("${freqRaw}") — impostata a 2`);
            }
          }

          parsed.push({ name, address, city, preferred_day, frequency_weeks });
        }
        setRows(parsed);
        setErrors(errs);
      } catch {
        toast.error("Errore nella lettura del file. Usa formato .xlsx o .xls");
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const onDrop = useCallback((e) => {
    e.preventDefault(); setDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) parseFile(file);
  }, []);

  const handleImport = async () => {
    if (!rows.length) return;
    setImporting(true);
    try {
      const res = await axios.post(`${API}/api/clients/bulk-import`, rows,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDone({ created: res.data.created, appointments: res.data.appointments_created });
      setRows([]); setFileName("");
      toast.success(`${res.data.created} clienti importati con ${res.data.appointments_created} appuntamenti!`);
    } catch {
      toast.error("Errore durante l'importazione");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-2xl mx-auto">
      <div className="mb-6 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-[#0F172A]">Import Clienti da Excel</h1>
          <p className="text-slate-400 text-sm">Carica clienti in blocco con appuntamenti automatici</p>
        </div>
        <button onClick={downloadTemplate} data-testid="download-template-btn"
          className="flex items-center gap-1.5 border border-slate-200 text-slate-600 hover:bg-slate-50 px-4 py-2 rounded-xl text-sm font-medium transition-colors flex-shrink-0">
          <Download className="w-4 h-4" /> Template
        </button>
      </div>

      {/* Format hint */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-5">
        <p className="font-semibold text-[#0F172A] text-sm mb-3">Formato richiesto (.xlsx / .xls)</p>
        <div className="grid grid-cols-5 gap-1.5 text-center text-[11px] mb-2">
          {[
            { col: "A", label: "Nome Cliente", req: true },
            { col: "B", label: "Indirizzo", req: false },
            { col: "C", label: "Città", req: false },
            { col: "D", label: "Giorno Preferito", req: false },
            { col: "E", label: "Frequenza sett.", req: false },
          ].map(({ col, label, req }) => (
            <div key={col} className={`rounded-lg py-2 px-1 border ${req ? "bg-blue-50 border-blue-200 text-blue-700 font-semibold" : "bg-white border-slate-200 text-slate-500"}`}>
              <div className="font-mono font-bold">{col}</div>
              <div className="mt-0.5 leading-tight">{label}</div>
              {req && <div className="text-[9px] text-blue-500 mt-0.5">obbligatorio</div>}
            </div>
          ))}
        </div>
        <div className="text-xs text-slate-500 space-y-0.5">
          <p>• <strong>Giorno Preferito</strong>: scrivi il nome (es. <em>Lunedì</em>, <em>Mer</em>, <em>Venerdì</em>) o un numero (1=Lun … 7=Dom)</p>
          <p>• <strong>Frequenza</strong>: ogni quante settimane visitare il cliente (es. <em>1</em>, <em>2</em>, <em>4</em>). Default: 2</p>
          <p>• Il primo appuntamento viene creato automaticamente nel primo giorno utile</p>
        </div>
      </div>

      {/* Drop zone */}
      {!rows.length && !done && (
        <div
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={`border-2 border-dashed rounded-2xl p-10 text-center transition-colors ${dragging ? "border-[#2563EB] bg-blue-50" : "border-slate-200 hover:border-slate-400"}`}
        >
          <FileSpreadsheet className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 text-sm font-medium mb-1">Trascina il file Excel qui</p>
          <p className="text-slate-400 text-xs mb-4">oppure</p>
          <label className="cursor-pointer bg-[#0F172A] text-white px-6 py-2.5 rounded-xl text-sm font-semibold hover:bg-slate-800 transition">
            Scegli file
            <input type="file" accept=".xlsx,.xls,.csv" className="hidden"
              onChange={e => { if (e.target.files?.[0]) parseFile(e.target.files[0]); }} />
          </label>
        </div>
      )}

      {/* Preview */}
      {rows.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-emerald-500" />
              <span className="font-semibold text-[#0F172A] text-sm">{fileName}</span>
              <span className="text-xs text-slate-400">({rows.length} clienti)</span>
            </div>
            <button onClick={() => { setRows([]); setFileName(""); setErrors([]); }} className="text-slate-400 hover:text-slate-600">
              <X className="w-4 h-4" />
            </button>
          </div>

          {errors.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-3 text-xs text-amber-700 space-y-1">
              <p className="font-semibold flex items-center gap-1.5">
                <AlertCircle className="w-4 h-4" />{errors.length} avvisi:
              </p>
              {errors.map((e, i) => <p key={i}>• {e}</p>)}
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-4">
            <div className="max-h-64 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b border-slate-100 sticky top-0">
                  <tr>
                    {["Nome", "Indirizzo", "Città", "Giorno", "Freq."].map(h => (
                      <th key={h} className="text-left px-3 py-2 text-slate-500 font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {rows.map((r, i) => (
                    <tr key={i} data-testid={`import-row-${i}`}>
                      <td className="px-3 py-2 text-[#0F172A] font-medium">{r.name}</td>
                      <td className="px-3 py-2 text-slate-500 truncate max-w-[100px]">{r.address || "—"}</td>
                      <td className="px-3 py-2 text-slate-500">{r.city || "—"}</td>
                      <td className="px-3 py-2">
                        {r.preferred_day !== null
                          ? <span className="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded-md font-medium">{DAY_LABELS[r.preferred_day]}</span>
                          : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-2">
                        <span className="bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-md font-medium">
                          {r.frequency_weeks}w
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Summary */}
          <div className="flex gap-3 text-xs text-slate-500 mb-4">
            <span className="bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-lg font-medium">
              {rows.filter(r => r.preferred_day !== null).length} con giorno preferito
            </span>
            <span className="bg-blue-50 text-blue-700 px-2.5 py-1 rounded-lg font-medium">
              {rows.length} appuntamenti saranno creati
            </span>
          </div>

          <div className="flex gap-2">
            <button onClick={() => { setRows([]); setFileName(""); }}
              className="flex-1 border border-slate-200 py-3 rounded-xl text-sm text-slate-600 hover:bg-slate-50 transition">
              Annulla
            </button>
            <button onClick={handleImport} disabled={importing} data-testid="confirm-import-btn"
              className="flex-1 bg-[#0F172A] text-white py-3 rounded-xl text-sm font-bold disabled:opacity-50 flex items-center justify-center gap-2 hover:bg-slate-800 transition">
              {importing
                ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Importazione...</>
                : <><Upload className="w-4 h-4" />Importa {rows.length} clienti</>}
            </button>
          </div>
        </div>
      )}

      {/* Success */}
      {done && (
        <div className="text-center py-10">
          <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-3" />
          <h2 className="text-xl font-bold text-[#0F172A] mb-1">{done.created} clienti importati!</h2>
          <p className="text-slate-500 text-sm mb-1">
            {done.appointments} appuntamenti creati automaticamente nel primo giorno utile.
          </p>
          <p className="text-slate-400 text-sm mb-5">
            Vai nella sezione Clienti per verificare i dettagli.
          </p>
          <button onClick={() => setDone(null)}
            className="border border-slate-200 px-6 py-2.5 rounded-xl text-sm text-slate-600 hover:bg-slate-50 transition">
            Importa un altro file
          </button>
        </div>
      )}
    </div>
  );
}

export default function ExcelUploadPage() {
  return (
    <PremiumGate>
      <UploadContent />
    </PremiumGate>
  );
}
